/**
 * Metafans BuddyPress Comments System
 * Handles posting, deleting, media uploads, and mentions in activity comments.
 */

import { postAction } from './api.js';

class CommentsSystem {
    constructor() {
        this.init();
    }

    init() {
        this.bindEvents();
    }

    bindEvents() {
        const doc = document;

        // --- UI Toggles ---

        // Toggle Main Comment Box (Focus on textarea)
        doc.addEventListener('click', (e) => {
            const btn = e.target.closest('.th-bp-post-comment-button a');
            if (btn) {
                e.preventDefault();
                e.stopPropagation();
                this.toggleCommentBox(btn);
            }
        });

        // Toggle Comments List (Show/Hide)
        doc.addEventListener('click', (e) => {
            const toggle = e.target.closest('.activity-comments-toggle');
            if (toggle) {
                e.preventDefault();
                e.stopPropagation();
                this.toggleCommentsList(toggle);
            }
        });

        // Toggle Reply Form (FIXED for Popup)
        doc.addEventListener('click', (e) => {
            const btn = e.target.closest('.comment-reply-form-toggle');
            if (btn) {
                e.preventDefault();
                this.toggleReplyForm(btn);
            }
        });

        // Load More Comments
        doc.addEventListener('click', (e) => {
            const btn = e.target.closest('.show-more-comments');
            if (btn) {
                e.preventDefault();
                this.loadMoreComments(btn);
            }
        });


        // --- Comment Submission ---

        // Submit Button Click
        doc.addEventListener('click', (e) => {
            const btn = e.target.closest('.th_media_comment_submit, .comment-submit-button');
            if (btn) {
                e.preventDefault();
                this.handleSubmitClick(btn);
            }
        });

        // Form Submit
        doc.addEventListener('submit', (e) => {
            const form = e.target.closest('.tophive-bp-comment-form');
            if (form) {
                e.preventDefault();
                this.handleFormSubmit(form);
            }
        });

        // Enter Key Press in Editable Div
        doc.addEventListener('keypress', (e) => {
            if (e.target.matches('.tophive-bp-comment-form .comments-text')) {
                if (e.which === 13 && !e.shiftKey) {
                    e.preventDefault();
                    const form = e.target.closest('form');
                    this.handleFormSubmit(form);
                }
            }
        });


        // --- Media Handling ---

        // File Input Change
        doc.addEventListener('change', (e) => {
            if (e.target.matches('.comment-upload-media')) {
                this.handleMediaUpload(e.target);
            }
        });

        // Remove Media
        doc.addEventListener('click', (e) => {
            const btn = e.target.closest('.remove-comment-media');
            if (btn) {
                this.removeMedia(btn);
            }
        });


        // --- Deletion ---

        doc.addEventListener('click', (e) => {
            const btn = e.target.closest('a.comment-delete');
            if (btn) {
                e.preventDefault();
                this.confirmDelete(btn);
            }
        });


        // --- Mentions ---

        // Initialize mentions on focus
        doc.addEventListener('focusin', (e) => {
            if (e.target.matches('.comments-text') && !e.target.classList.contains('mention-enabled')) {
                this.initMentions(e.target);
            }
        });
    }

    // =========================================
    // UI Logic
    // =========================================

    toggleCommentBox(btn) {
        // Support both Feed (.activity-item) and Lightbox (.th-media-viewer-container) contexts
        const rootItem = btn.closest('.activity-item') || btn.closest('.th-media-viewer-container');
        if (!rootItem) return;

        const el = rootItem.querySelector('.comments-text');
        const commentsList = rootItem.querySelector('.activity-comments');

        // Toggle list visibility
        if (commentsList) {
            commentsList.classList.toggle('show');
            // Ensure display style is cleared if class handles it, or force block
            if (commentsList.classList.contains('show')) {
                commentsList.style.display = 'block';
            } else {
                commentsList.style.display = '';
            }
        }

        if (el) {
            // Focus and move caret to end
            el.focus();
            if (window.getSelection && document.createRange) {
                const selection = window.getSelection();
                const range = document.createRange();
                selection.removeAllRanges();
                range.selectNodeContents(el);
                range.collapse(false);
                selection.addRange(range);
            }
        }
    }

    /**
     * Toggles the reply form with context awareness.
     * Fixes issue where Popup reply button opened the Feed reply box (duplicate IDs).
     */
    toggleReplyForm(btn) {
        const targetId = btn.getAttribute('href'); // e.g., #ac-form-1234
        if (!targetId) return;

        let target = null;

        // 1. Determine Context (Popup or Feed)
        const context = btn.closest('.th-media-viewer-container') || btn.closest('.activity-item');

        // 2. Search inside context first (Scoped Query)
        if (context) {
            target = context.querySelector(targetId);
        }

        // 3. Fallback to global search if context failed
        if (!target) {
            target = document.querySelector(targetId);
        }

        if (target) {
            target.classList.toggle('show');
            // Auto-focus the inner textarea/div
            const input = target.querySelector('.comments-text') || target.querySelector('textarea');
            if (input) input.focus();
        }
    }

    toggleCommentsList(btn) {
        let targetId = btn.getAttribute('href');
        if (!targetId || targetId === '#') {
            const nextLink = btn.nextElementSibling; // legacy fallback
            if (nextLink) targetId = nextLink.getAttribute('href');
        }

        if (targetId) {
            const target = document.querySelector(targetId);
            const loadMore = document.querySelector(`${targetId} + .show-more-comments`);

            if (target) {
                const isHidden = window.getComputedStyle(target).display === 'none';
                target.style.display = isHidden ? 'block' : 'none';
            }
            if (loadMore) {
                const isHidden = window.getComputedStyle(loadMore).display === 'none';
                loadMore.style.display = isHidden ? 'block' : 'none';
            }
        }
    }

    // =========================================
    // Submission Logic
    // =========================================

    handleSubmitClick(btn) {
        const form = btn.closest('form');
        if (form) {
            // Standard form context
            this.handleFormSubmit(form);
        } else {
            // Media Viewer context
            const wrapper = document.querySelector('.media_comment_box');
            if (!wrapper) return;

            const textarea = wrapper.querySelector('.comment_text textarea');
            const commentText = textarea ? textarea.value : '';
            const mediaId = btn.dataset.mediaId;
            const activityId = btn.dataset.activityId;

            if (commentText || mediaId) {
                btn.disabled = true;
                this.postNewMediaComment(commentText, mediaId, activityId, btn);
            }
        }
    }

    handleFormSubmit(form) {
        const textEl = form.querySelector('.comments-text');
        let text = textEl ? textEl.innerHTML : '';
        const activityId = form.dataset.activityId;
        const commentId = form.dataset.commentId || ''; // Parent comment ID if reply
        const type = form.dataset.type || 'postComment';

        // Check for attached media URL hidden input
        const mediaInput = document.getElementById(`comment-media-url-${activityId}`);
        const mediaUrl = mediaInput ? mediaInput.value : '';

        if (mediaUrl) {
            text += `<div class="comment-media-section"><img src="${mediaUrl}" alt="" /></div>`;
        }

        if (!text.trim() && !mediaUrl) return;

        this.postActivityComment(activityId, text, commentId, type);
    }

    async postActivityComment(activityId, commentText, commentId, type) {
        const currentOpenComments = this.countOpenComments(activityId);

        // Visual Feedback
        const activityItem = document.querySelector(`#activity-${activityId}`);
        const editableText = activityItem?.querySelector('.comments-text');
        if (activityItem) activityItem.classList.add('commenting');
        if (editableText) editableText.setAttribute('contenteditable', 'false');

        try {
            const response = await postAction('tophive_bp_activity_comment', {
                comment_text: commentText,
                activity_id: activityId,
                comment_id: commentId,
                type: type,
                cur_num_of_open_cmnt: currentOpenComments
            });

            if (response.html) {
                // Update Feed
                const streamList = document.querySelector(`#activity-${activityId} .activity-comments`);
                if (streamList) streamList.innerHTML = response.html;

                // Update Lightbox (Popup)
                const lightboxList = document.querySelector('.th-media-viewer-container.show .comment_section');
                if (lightboxList) lightboxList.innerHTML = response.html;

                // Reset Input in Feed
                if (editableText) {
                    editableText.innerHTML = '';
                    editableText.setAttribute('contenteditable', 'true');
                }

                // Reset Input in Lightbox
                const lightboxInput = document.querySelector('.th-media-viewer-container.show .tophive-bp-comment-form .comments-text');
                if (lightboxInput) {
                    lightboxInput.innerHTML = '';
                }

                // Clear media preview
                const mediaPreview = document.querySelector(`.comments-media-preview-${activityId}`);
                if (mediaPreview) mediaPreview.innerHTML = '';
                const mediaInput = document.getElementById(`comment-media-url-${activityId}`);
                if (mediaInput) mediaInput.value = '';
            }

            // --- FIX: Update Comment Count in ALL Locations (Feed + Popup) ---
            if (response.count) {
                const metaCounts = document.querySelectorAll(`.activity-comments-meta-${activityId}`);
                metaCounts.forEach(el => el.innerHTML = response.count);
            }

        } catch (err) {
            console.error('Comment post failed', err);
            if (editableText) editableText.setAttribute('contenteditable', 'true');
        } finally {
            if (activityItem) activityItem.classList.remove('commenting');
        }
    }

    async postNewMediaComment(commentText, mediaId, activityId, btn) {
        try {
            const response = await postAction('th_bp_media_comments_post', {
                comment_text: commentText,
                media_id: mediaId,
                activity_id: activityId
            });

            if (response === true || response.success) {
                const wrapper = document.querySelector('.media_comment_box');
                if (wrapper) wrapper.querySelector('textarea').value = '';
                btn.disabled = false;
                this.refreshMediaComments(mediaId, activityId);
            }
        } catch (err) {
            console.error(err);
            btn.disabled = false;
        }
    }

    async refreshMediaComments(mediaId, activityId) {
        try {
            const response = await postAction('th_bp_media_comments', { media_id: mediaId, activity_id: activityId });
            if (response && response.data) {
                const viewer = document.querySelector('.th-media-viewer-container.show');
                if (viewer) {
                    // Update Comments
                    const commentSection = viewer.querySelector('.comment_section');
                    if (commentSection && response.data.comments) {
                        commentSection.innerHTML = response.data.comments;
                    }

                    // Update Footer (Counts & Buttons)
                    const footerSection = viewer.querySelector('.activity-footer-links');
                    if (footerSection && response.data.activity_footer) {
                        footerSection.outerHTML = response.data.activity_footer;
                    }
                }
            }
        } catch (e) { }
    }

    async loadMoreComments(btn) {
        const show = btn.dataset.show;
        const activityId = btn.dataset.activityId;

        try {
            const response = await postAction('tophive_bp_more_comments', {
                show,
                activity_id: activityId
            });

            const streamList = document.querySelector(`#activity-${activityId} .activity-comments`);
            if (streamList) streamList.innerHTML = response;

            const lightboxList = document.querySelector('.th-media-viewer-container.show .comment_section');
            if (lightboxList) lightboxList.innerHTML = response;

        } catch (err) {
            console.error('Failed to load more comments', err);
        }
    }

    countOpenComments(activityId) {
        const container = document.querySelector(`#activity-${activityId} .activity-comments > ul`);
        return container ? container.children.length : 0;
    }

    // =========================================
    // Media Upload Logic
    // =========================================

    handleMediaUpload(input) {
        if (!input.files || !input.files[0]) return;

        const activityId = input.dataset.id;
        const file = input.files[0];
        const formData = new FormData();

        formData.append('action', 'activity_upload');
        formData.append('file_name', file.name);
        formData.append('upload_file', file);

        const context = input.closest('.th-media-viewer-container') || document;
        const previewContainer = context.querySelector(`.comments-media-preview-${activityId}`);

        if (previewContainer) {
            previewContainer.classList.add('media-uploading');
        }

        fetch(Metafans_JS.ajaxurl, {
            method: 'POST',
            body: formData
        })
            .then(res => res.json())
            .then(data => {
                const allPreviews = document.querySelectorAll(`.comments-media-preview-${activityId}`);

                allPreviews.forEach(el => {
                    el.classList.add('done', data.type);
                    el.innerHTML = data.html;

                    const removeBtn = document.createElement('span');
                    removeBtn.className = 'remove-comment-media';
                    removeBtn.dataset.activityId = activityId;
                    removeBtn.dataset.attachmentId = data.id;
                    removeBtn.textContent = 'x';
                    el.appendChild(removeBtn);
                });

                const urlInput = document.getElementById(`comment-media-url-${activityId}`);
                if (urlInput) urlInput.value = data.url;
            })
            .catch(err => console.error('Upload failed', err));
    }

    async removeMedia(btn) {
        const attachmentId = btn.dataset.attachmentId;
        const activityId = btn.dataset.activityId;
        const allPreviews = document.querySelectorAll(`.comments-media-preview-${activityId}`);

        allPreviews.forEach(el => {
            el.classList.remove('media-uploading', 'done');
            el.style.display = 'none';
        });

        try {
            await postAction('th_bp_remove_media', { att_id: attachmentId });
            allPreviews.forEach(el => {
                el.innerHTML = '';
                el.style.display = 'block';
            });
            const urlInput = document.getElementById(`comment-media-url-${activityId}`);
            if (urlInput) urlInput.value = '';
        } catch (err) {
            console.error(err);
        }
    }

    // =========================================
    // Delete Logic
    // =========================================

    confirmDelete(btn) {
        const markup = `
            <div class="mf-alert-popup activity-delete open" id="delete_cmnt_modal">
                <div class="mf-alert-popup-container">
                    <div class="alert-popup-head"><h6>Delete Comment?</h6></div>
                    <div class="alert-popup-content">
                        <span class="content">If you delete this Comment, you won't be able to restore it.</span>
                    </div>
                    <div class="alert-popup-footer">
                        <button class="popup-cancel">Cancel</button>
                        <button class="popup-yes confirm-delete-cmnt">Delete</button>
                    </div>
                </div>
            </div>`;

        document.body.insertAdjacentHTML('beforeend', markup);

        const modal = document.getElementById('delete_cmnt_modal');
        const confirmBtn = modal.querySelector('.confirm-delete-cmnt');
        const cancelBtn = modal.querySelector('.popup-cancel');

        const cleanup = () => modal.remove();

        cancelBtn.addEventListener('click', cleanup);

        confirmBtn.addEventListener('click', () => {
            this.deleteComment(
                btn.dataset.activityId,
                btn.dataset.commentId,
                btn.dataset.replyId
            );
            cleanup();
        });
    }

    async deleteComment(activityId, commentId, replyId) {
        try {
            const response = await postAction('tophive_bp_delete_comment', {
                activity_id: activityId,
                comment_id: commentId,
                reply_id: replyId
            });

            if (response.html) {
                // Update Feed
                const streamList = document.querySelector(`#activity-${activityId} .activity-comments`);
                if (streamList) streamList.innerHTML = response.html;

                // Update Popup
                const lightboxList = document.querySelector('.th-media-viewer-container.show .comment_section');
                if (lightboxList) lightboxList.innerHTML = response.html;
            }

            // --- FIX: Update Comment Count in ALL Locations (Feed + Popup) ---
            if (response.count) {
                const metaCounts = document.querySelectorAll(`.activity-comments-meta-${activityId}`);
                metaCounts.forEach(el => el.innerHTML = response.count);
            }
        } catch (err) {
            console.error('Delete failed', err);
        }
    }

    // =========================================
    // Utils
    // =========================================

    initMentions(element) {
        if (typeof jQuery !== 'undefined' && window.bp && window.bp.mentions) {
            jQuery(element).bp_mentions(window.bp.mentions.users);
            element.classList.add('mention-enabled');
        }
    }
}

export function initComments() {
    return new CommentsSystem();
}